import { selectIsAiBusinessToolsEnabled } from 'app-components/flux/ai-chat/aiChatSelectors';
import { resolveCustomer, resolveProject } from 'app-components/flux/ai-chat/tools/businessNavigation';
import { customersSelectors } from 'app-components/flux/customers';
import type RootState from 'app-components/utils/redux-store/rootState';

import { mileageTrackerPath } from 'containers/App/paths';

import { registerNavigationIntent } from './registry';
import type { NavigationIntent, NavigationResult } from './types';

const isAvailable = selectIsAiBusinessToolsEnabled;

const archivedClientError = (name: string): NavigationResult => ({
  type: 'error',
  reason: `${name} is archived, and archived clients have no page to open. Unarchive the client on the Clients & Projects page to view it.`,
});

const toErrorResult = (error: unknown): NavigationResult => ({
  type: 'error',
  reason: error instanceof Error ? error.message : 'Could not resolve the requested page.',
});

const createStaticBusinessIntent = (
  name: string,
  description: string,
  path: string,
): NavigationIntent<Record<string, never>, RootState> => ({
  name,
  description,
  isAvailable,
  resolver: () => ({ type: 'navigate', payload: { path } }),
});

type ClientArgs = { customerId?: string; customerName?: string };
type ProjectArgs = { projectId?: string; projectName?: string };

const clientDetailsIntent: NavigationIntent<ClientArgs, RootState> = {
  name: 'client_details',
  description: "One client's details, invoices and balance.",
  isAvailable,
  params: {
    definitions: [
      { name: 'customerId', type: 'string', description: 'Client id from get_customers.' },
      { name: 'customerName', type: 'string', description: 'Client name, used when no customerId is known.' },
    ],
  },
  resolver: (args, state) => {
    try {
      const customer = resolveCustomer(state, args ?? {});
      if (customer.isArchived)
        return archivedClientError(customersSelectors.getCustomerNameById(state, customer.id ?? ''));
      return { type: 'navigate', payload: { path: `/clients/${encodeURIComponent(customer.id ?? '')}` } };
    } catch (error) {
      return toErrorResult(error);
    }
  },
};

const projectDetailsIntent: NavigationIntent<ProjectArgs, RootState> = {
  name: 'project_details',
  description: "One project's details, status and unbilled work.",
  isAvailable,
  params: {
    definitions: [
      { name: 'projectId', type: 'string', description: 'Project id from get_projects.' },
      { name: 'projectName', type: 'string', description: 'Project name, used when no projectId is known.' },
    ],
  },
  resolver: (args, state) => {
    try {
      const project = resolveProject(state, args ?? {});
      if (!project.customerId) {
        return { type: 'error', reason: `Project "${project.name}" is not linked to a client, so it has no page.` };
      }
      if (customersSelectors.getCustomerById(state, project.customerId)?.isArchived) {
        return archivedClientError(customersSelectors.getCustomerNameById(state, project.customerId));
      }
      const path = `/clients/${encodeURIComponent(project.customerId)}/${encodeURIComponent(project.id ?? '')}`;
      return { type: 'navigate', payload: { path } };
    } catch (error) {
      return toErrorResult(error);
    }
  },
};

const businessIntents = [
  createStaticBusinessIntent('clients', 'Clients & Projects page listing all clients.', '/clients'),
  createStaticBusinessIntent('invoices', 'Invoices list under Sales & Billing.', '/sales-and-billing/invoices'),
  createStaticBusinessIntent('estimates', 'Estimates list under Sales & Billing.', '/sales-and-billing/estimates'),
  createStaticBusinessIntent(
    'sales_receipts',
    'Sales receipts list under Sales & Billing.',
    '/sales-and-billing/sales-receipts',
  ),
  createStaticBusinessIntent(
    'products_and_services',
    'Products and services catalog.',
    '/sales-and-billing/products-and-services',
  ),
  createStaticBusinessIntent('mileage', 'Mileage tracker trips.', mileageTrackerPath),
];

for (const intent of businessIntents) {
  registerNavigationIntent(intent);
}
registerNavigationIntent(clientDetailsIntent);
registerNavigationIntent(projectDetailsIntent);
